import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  Center,
  Container,
  Divider,
  Loader,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconCoin,
  IconLock,
  IconMail,
} from "@tabler/icons-react";
import type { AlphaGroupDetail } from "@smartwiz/shared-types";
import SmartWizLogo from "@/components/Logos/SmartWizLogo";
import { GoogleLogo } from "@/components/Logos/GoogleLogo";
import { useAuthStore } from "@utils/stores/authStore";
import { login } from "@/pages/LoginManagement/api";
import { useGoogleSignIn } from "@/pages/LoginManagement/hooks/useGoogleSignIn";
import type { LoginApiResponse } from "@/pages/LoginManagement/types/login.types";
import { isBusinessSelectionResponse } from "@/pages/LoginManagement/types/login.types";
import { AppRoutes } from "@/constants";
import { openExtensionToSettingsPage } from "@/utils/extensionClient";
import { getAlphaGroup, enrollInAlpha } from "./api";
import "./AlphaSignupPage.scss";

const AlphaSignupPage = (): JSX.Element => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, refreshAuth, loading: authLoading } = useAuthStore();

  const [alphaGroup, setAlphaGroup] = useState<AlphaGroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [loginPending, setLoginPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingEnroll, setPendingEnroll] = useState(false);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const [enrolledAsWaitlist, setEnrolledAsWaitlist] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    refreshAuth().catch(() => {});
  }, []);

  const authCheckDone = !authLoading;
  useEffect(() => {
    if (!slug || !authCheckDone) return;
    setLoading(true);
    const fetchAlpha = async () => {
      try {
        const data = await getAlphaGroup(slug);
        setAlphaGroup(data);
      } catch {
        setError("Alpha program not found");
      } finally {
        setLoading(false);
      }
    };
    fetchAlpha();
  }, [slug, authCheckDone]);

  useEffect(() => {
    if (user && !authLoading && pendingEnroll && slug) {
      handleEnroll();
      setPendingEnroll(false);
    }
  }, [user, authLoading, pendingEnroll]);

  const handleEnroll = async () => {
    if (!slug || enrolling) return;
    setEnrolling(true);
    try {
      const result = await enrollInAlpha(slug);
      setEnrolledAsWaitlist(result.enrollmentStatus === "waitlisted");
      setEnrollmentSuccess(true);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message || e?.message || "Enrollment failed";
      if (msg.toLowerCase().includes("already enrolled")) {
        setEnrollmentSuccess(true);
        return;
      }
      showNotification({
        title: "Enrollment failed",
        message: msg,
        color: "red",
      });
    } finally {
      setEnrolling(false);
    }
  };

  const handleGoogleSuccess = (response: LoginApiResponse) => {
    if (isBusinessSelectionResponse(response)) {
      showNotification({
        title: "Multiple accounts",
        message:
          "Please sign in via the login page first, then return to this link.",
        color: "orange",
      });
      return;
    }
    setPendingEnroll(true);
    refreshAuth();
  };

  const handleGoogleError = (err: any) => {
    showNotification({
      title: "Google Sign-In Failed",
      message:
        err?.content?.message ||
        err?.message ||
        "Failed to sign in with Google",
      color: "red",
    });
  };

  const { triggerSignIn: triggerGoogleSignIn } = useGoogleSignIn(
    handleGoogleSuccess,
    handleGoogleError,
    { allowCreate: false }
  );

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginPending(true);
    try {
      await login({
        email: email.toLowerCase(),
        password,
        skipRedirect: true,
      });
      setPendingEnroll(true);
      await refreshAuth();
    } catch (err: any) {
      showNotification({
        title: "Sign-in failed",
        message: err?.details || err?.message || "Invalid email or password",
        color: "red",
      });
    } finally {
      setLoginPending(false);
    }
  };

  if (loading || authLoading) {
    return (
      <Center style={{ height: "100vh" }}>
        <SmartWizLogo />
        <Loader size="md" color="blue" ml={10} />
      </Center>
    );
  }

  if (error || !alphaGroup) {
    return (
      <Center style={{ height: "100vh" }}>
        <Stack align="center" gap="md">
          <SmartWizLogo />
          <Title order={3}>Alpha Program Not Found</Title>
          <Text c="dimmed">This alpha program doesn't exist or has ended.</Text>
          <Button variant="light" onClick={() => navigate(AppRoutes.login)}>
            Go to Login
          </Button>
        </Stack>
      </Center>
    );
  }

  const formattedStartDate = new Date(
    alphaGroup.startDate + "T00:00:00"
  ).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const enrollButtonLabel = alphaGroup.hasStarted
    ? "Join Alpha Program"
    : "Join Waitlist";

  const renderRightPanel = () => {
    // Enrollment success
    if (enrollmentSuccess) {
      const isWaitlist = enrolledAsWaitlist || !alphaGroup.hasStarted;
      return (
        <Stack gap="lg" align="center" ta="center">
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: isWaitlist ? "#fff3e0" : "#e6f9ee",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isWaitlist ? (
              <IconClock size={32} color="#f57c00" />
            ) : (
              <IconCheck size={32} color="#28a745" />
            )}
          </div>
          <Title order={3}>
            {isWaitlist ? "You're on the waitlist!" : "You're in!"}
          </Title>
          <Text c="dimmed">
            {isWaitlist ? (
              <>
                You've been added to the waitlist for <b>{alphaGroup.name}</b>.
                The alpha starts <b>{formattedStartDate}</b> — your access will
                be activated automatically.
              </>
            ) : (
              <>
                You've been enrolled in the <b>{alphaGroup.name}</b> alpha
                program. The feature is now available for your entire team.
              </>
            )}
          </Text>
          <Button
            size="lg"
            radius="md"
            onClick={() => openExtensionToSettingsPage("settings-alpha-groups")}
          >
            Open in Extension
          </Button>
          <Button
            variant="subtle"
            size="sm"
            onClick={() => navigate(AppRoutes.root, { replace: true })}
          >
            Go to Dashboard
          </Button>
        </Stack>
      );
    }

    // Authenticated + already enrolled
    if (user && alphaGroup.enrollmentStatus) {
      const isWaitlisted = alphaGroup.enrollmentStatus === "waitlisted";
      return (
        <Stack gap="lg">
          <Title order={3}>
            {isWaitlisted ? "You're on the waitlist!" : "You're already in!"}
          </Title>
          <Text c="dimmed">Signed in as {user.email}</Text>
          <Text>
            {isWaitlisted ? (
              <>
                You're on the waitlist for the {alphaGroup.name} alpha. Access
                starts <b>{formattedStartDate}</b>.
              </>
            ) : (
              <>You're enrolled in the {alphaGroup.name} alpha.</>
            )}
          </Text>
          <Button
            size="lg"
            radius="md"
            onClick={() => navigate(AppRoutes.root, { replace: true })}
          >
            Go to Dashboard
          </Button>
        </Stack>
      );
    }

    // Authenticated + not enrolled
    if (user) {
      return (
        <Stack gap="lg">
          <Title order={3}>
            {alphaGroup.hasStarted
              ? "Join the Alpha Program"
              : "Join the Waitlist"}
          </Title>
          <Text c="dimmed">Signed in as {user.email}</Text>
          {!alphaGroup.hasStarted && (
            <Badge
              color="orange"
              variant="light"
              size="lg"
              leftSection={<IconCalendar size={14} />}
            >
              Starts {formattedStartDate}
            </Badge>
          )}
          <Text>
            {alphaGroup.hasStarted
              ? `By joining, you'll get early access to ${alphaGroup.name}.`
              : `Join the waitlist to get access when ${alphaGroup.name} launches.`}
          </Text>
          <Button
            size="lg"
            radius="md"
            onClick={handleEnroll}
            loading={enrolling}
          >
            {enrollButtonLabel}
          </Button>
          <Text size="sm" c="dimmed">
            You can manage alpha programs anytime from{" "}
            <b>Settings &gt; Alpha Programs</b> in the SmartWiz extension or web
            app.
          </Text>
        </Stack>
      );
    }

    // Not authenticated — show login form
    return (
      <form onSubmit={handleLoginSubmit}>
        <Stack gap="md">
          <Title order={3}>
            {alphaGroup.hasStarted
              ? "Join the Alpha Program"
              : "Join the Waitlist"}
          </Title>
          <Text c="dimmed" size="sm">
            Sign in to enroll
          </Text>
          {!alphaGroup.hasStarted && (
            <Badge
              color="orange"
              variant="light"
              size="lg"
              leftSection={<IconCalendar size={14} />}
            >
              Starts {formattedStartDate}
            </Badge>
          )}

          <button
            type="button"
            onClick={triggerGoogleSignIn}
            className="google-signin-btn"
          >
            <GoogleLogo width={18} height={18} style={{ marginRight: "8px" }} />
            Sign in with Google
          </button>

          <div className="divider-row">
            <div className="divider-line" />
            <Text
              size="sm"
              c="dimmed"
              style={{ margin: "0 16px", whiteSpace: "nowrap" }}
            >
              or
            </Text>
            <div className="divider-line" />
          </div>

          <TextInput
            leftSection={<IconMail color="#A3A9B0" />}
            placeholder="Enter your account email"
            name="email"
            radius="md"
            type="text"
            value={email}
            required
            variant="unstyled"
            onChange={(e) => setEmail(e.target.value)}
          />
          <PasswordInput
            leftSection={<IconLock color="#A3A9B0" />}
            placeholder="Enter password"
            name="password"
            radius="md"
            value={password}
            required
            variant="unstyled"
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            size="lg"
            radius="md"
            fullWidth
            loading={loginPending}
            mt="sm"
          >
            {alphaGroup.hasStarted
              ? "Sign In & Enroll"
              : "Sign In & Join Waitlist"}
          </Button>
        </Stack>
      </form>
    );
  };

  return (
    <div className="alpha-signup-container">
      <div className="alpha-signup-hero">
        <Stack align="center" justify="flex-start" gap="lg" p="xl">
          <SmartWizLogo />
          <Title order={2} c="white" ta="center" maw={500}>
            {alphaGroup.name}
          </Title>

          {!alphaGroup.hasStarted && (
            <Badge
              color="yellow"
              variant="filled"
              size="lg"
              leftSection={<IconCalendar size={14} />}
            >
              Starting {formattedStartDate}
            </Badge>
          )}

          <div className="alpha-signup-section">
            <Title order={4} c="white" mb="xs">
              What to Expect
            </Title>
            <Text
              c="rgba(255,255,255,0.9)"
              size="sm"
              style={{ whiteSpace: "pre-line" }}
            >
              {alphaGroup.whatToExpect}
            </Text>
          </div>

          <Divider color="rgba(255,255,255,0.2)" w="100%" maw={500} />

          <Card
            radius="md"
            p="md"
            maw={500}
            w="100%"
            style={{ backgroundColor: "rgba(255,255,255,0.12)" }}
          >
            <Stack gap="xs">
              <Title
                order={4}
                c="white"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <IconCoin size={20} /> Pricing
              </Title>
              <Text c="rgba(255,255,255,0.9)" size="sm">
                {alphaGroup.pricingInfo}
              </Text>
            </Stack>
          </Card>
        </Stack>
      </div>
      <Container className="alpha-signup-form" size="sm">
        {renderRightPanel()}
      </Container>
    </div>
  );
};

export default AlphaSignupPage;
